import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'
import useLanguage from '@/hooks/use-language'
import useMediaQuery from '@/hooks/use-media-query'
import BackgroundImage from '@/components/background-image'

import mapImageMobile from './images/map-mobile@3x.png'
import mapImage from './images/map@3x.png'

const CitySelector = dynamic(() => import('@/features/CitySelector'))

const ratio = 0.9712
const mobileRatio = 1.02

const HeroWrapper = styled.div`
  --hero-height: 926px;
  --hero-ratio: ${ratio};
  --hero-fixed: 0px;
  background-color: ${colors.background};
  min-height: var(--hero-height);
  position: relative;
  overflow: hidden;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    --hero-fixed: 292px;
    --hero-height: calc(var(--hero-fixed) + var(--hero-ratio) * 100vw);
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    --hero-ratio: ${mobileRatio};
    --hero-fixed: 444px;
  }
  @media (max-width: 375px) {
    --hero-fixed: 472px;
  }
`

const BackgroundWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: clamp(440px, (100vw - 545px) * ${ratio}, 720px);
  bottom: 40px;
  right: 0;
  margin-right: 18vw;

  @media (max-width: 2100px) {
    right: 235px;
    margin-right: 0;
  }

  @media (max-width: ${breakpoints.bigDesktop}) {
    right: 140px;
  }

  @media (max-width: ${breakpoints.mapLarge}) {
    right: 0;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    width: calc(100vw * (345 / 600));
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 0;
    height: 368px;
    width: 100%;
    bottom: -15px;
  }
`

const HeroContainer = styled.div`
  position: relative;
  max-width: 590px;
  margin-top: 216px;
  margin-bottom: auto;
  margin-left: 18vw;
  @media (max-width: 2100px) {
    margin-left: 235px;
  }
  @media (max-width: ${breakpoints.bigDesktop}) {
    max-width: 570px;
    margin-left: 140px;
  }
  @media (max-width: 1470px) {
    max-width: 414px;
  }
  @media (max-width: ${breakpoints.card}) {
    margin-top: 160px;
    margin-left: 112px;
    max-width: 635px;
  }
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 80px 0 0 60px;
    max-width: 500px;
    font-size: 56px;
    line-height: 62px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    max-width: 462px;
    margin: 32px 25px 0;
  }
`

const HeroHeader = styled.h1<{ currentLang: string }>`
  margin: 0;
  color: ${colors.regular};
  font-family: futura-pt-bold, Futura, sans-serif;
  font-size: 80px;
  font-weight: 700;
  letter-spacing: -0.17px;
  line-height: 76px;
  @media (max-width: 1365px) {
    font-size: 80px;
    line-height: ${props => (props.currentLang === 'zh' ? 82 : 72)}px;
  }
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 64px;
    line-height: ${props => (props.currentLang === 'zh' ? 72 : 62)}px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 52px;
    letter-spacing: -0.1px;
    line-height: ${props => (props.currentLang === 'zh' ? 60 : 50)}px;
  }
`

const HeroTagline = styled.div`
  margin: 30px 0 60px;
  color: ${colors.regular};
  font-size: 24px;
  line-height: 33px;
  font-weight: 600;
  letter-spacing: -0.2px;
  @media (max-width: ${breakpoints.card}) {
    font-size: 20px;
    line-height: 27px;
    max-width: 375px;
  }
  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 24px;
    max-width: unset;
    margin: 12px 0 30px;
  }
`

const SearchWrapper = styled.div`
  width: 100%;
  padding: 0 25px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 110px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin-top: 70px;
  }
`

const Button = styled.button`
  background: none;
  border: none;
  padding: 0;
`

const ListingButton = styled.button`
  text-decoration: none;
  border-radius: 6px;
  height: 50px;
  margin: 0;
  cursor: pointer;
  background-color: #132b3e;
  border: 2px solid #132b3e;
  color: #ffffff;
  padding: 16px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0px 2px 4px rgba(19, 43, 62, 0.25),
    0px 6px 6px 2px rgba(19, 43, 62, 0.1);

  &:hover {
    opacity: 0.9;
  }
`

const CurveStyler = styled.div`
  background-color: ${colors.white};
  width: 100%;
  height: 25px;
  border-radius: 25px 25px 0 0;
  bottom: 0;
  position: absolute;
  box-shadow: 0px 5px 8px 0px rgb(42 39 35 / 12%);

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    border-radius: 10px 10px 0 0;
    height: 10px;
    box-shadow: 0px 3px 12px 0px rgb(42 39 35 / 15%);
  }
`

const HeroSection = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('renters')
  const { language } = useLanguage()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  return (
    <HeroWrapper>
      <BackgroundWrapper>
        <Button aria-label={t('renters:Show me listings')} onClick={onClick}>
          <BackgroundImage
            priority
            name="renter-page-hero"
            images={[mapImage, mapImageMobile]}
            breakpoints={[breakpoints.phoneMax]}
            sizes={['720px', '100vw']}
            placeholder="empty"
          />
        </Button>
      </BackgroundWrapper>
      <SearchWrapper>
        {displayMobile && (
          <CitySelector
            instanceId="renters-select-city"
            id="renters-select-city"
          />
        )}
      </SearchWrapper>
      <HeroContainer>
        <HeroHeader currentLang={language.value}>
          {t("renters:Canada's safest rental website")}
        </HeroHeader>
        <HeroTagline>
          {t(
            `renters:Experience an easier and safer way to rent with verified landlords and easy-to-use features that are always free for renters like you.`
          )}
        </HeroTagline>
        <ListingButton id="btn-listings-renters-hero" onClick={onClick}>
          {t('renters:Show me listings')}
        </ListingButton>
      </HeroContainer>
      <CurveStyler />
    </HeroWrapper>
  )
}

export default HeroSection
